/**
 * @fileoverview Module — the Workshop-style screen root for a routed view.
 *
 * Mirrors the Workshop reference's `Module` primitive: a full-height column
 * that pins an optional <ModuleHeader> at the top with the <Page> body flowing
 * beneath it. Inherits the app theme (light, or dark via the shell's
 * `body.bp6-dark` toggle) — it is deliberately NOT the dark `WorkshopView`
 * authoring replica.
 *
 * Composition:
 *   <Module>
 *     <ModuleHeader title icon toolbar={…} />
 *     <Page>
 *       <Section>…<SectionCard>…</SectionCard></Section>
 *     </Page>
 *   </Module>
 *
 * CSS lives in src/styles/components/_module.scss (@layer ironcase).
 */

import type { ReactElement, ReactNode } from "react";

interface ModuleProps {
  children: ReactNode;
  /**
   * The module's optional persistent header (a `<ModuleHeader>`), rendered as a
   * sibling above the Page — the Workshop "Header" layer. Omit it and the module
   * has no header (Workshop: the header is optional, at most one per module).
   */
  header?: ReactNode;
  className?: string;
  /** Override the root test id (defaults to "module"). */
  "data-testid"?: string;
}

export function Module({
  children,
  header,
  className,
  "data-testid": testId = "module",
}: ModuleProps): ReactElement {
  return (
    <div
      data-testid={testId}
      className={["bp-module", className].filter(Boolean).join(" ")}
    >
      {header}
      {children}
    </div>
  );
}
